import React from 'react';
import {
  Card,
  Box,
  Typography,
  Chip,
  Switch,
  Slider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ScoringWeightConfig } from '../../models/tenant';

/**
 * Normalized configuration with calculated display percentage
 */
export interface NormalizedScoringFactorConfig extends ScoringWeightConfig {
  displayPercentage: number;
}

/**
 * Props for the ScoringFactorCard memoized component
 */
export interface ScoringFactorCardProps {
  component: string;
  config: ScoringWeightConfig;
  normalizedConfig: NormalizedScoringFactorConfig;
  isDragging: boolean;
  onToggle: (enabled: boolean) => void;
  onChange: (newValue: number) => void;
  onChangeCommitted: () => void;
}

/**
 * Memoized scoring factor card component to prevent unnecessary re-renders during slider drag
 * Only this component and affected sliders will re-render, not the entire scoring weights list
 * 
 * @param component - Unique identifier for this scoring factor
 * @param config - The scoring factor configuration object
 * @param normalizedConfig - The normalized configuration with calculated display percentage
 * @param isDragging - Whether this slider is currently being dragged
 * @param onToggle - Callback when the factor is enabled/disabled
 * @param onChange - Callback when the slider value changes during drag
 * @param onChangeCommitted - Callback when the slider drag is released
 */
const ScoringFactorCard = React.memo<ScoringFactorCardProps>(({
  component,
  config,
  normalizedConfig,
  onToggle,
  onChange,
  onChangeCommitted
}) => {
  const theme = useTheme();
  const isEnabled = config.value > 0;

  return (
    <Card key={component} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <Box sx={{ p: 1, mr: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={isEnabled}
              onChange={(e) => onToggle(e.target.checked)}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {config.label}
            </Typography>
          </Box>
          <Chip
            label={isEnabled ? `${Math.round(normalizedConfig.displayPercentage || config.value * 100)}%` : '0%'}
            size="small"
            sx={{
              color: theme.palette.common.white,
              backgroundColor: isEnabled ? theme.palette.grey[900] : theme.palette.grey[500],
              fontWeight: 600,
              minWidth: 60
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 7 }}>
          {config.description}
        </Typography>
        {isEnabled && (
          <Box sx={{ ml: 7, mr: 7 }}>
            <Slider
              value={normalizedConfig.displayPercentage ?? 0}
              onChange={(_, newValue) => onChange(newValue as number)}
              onChangeCommitted={onChangeCommitted}
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value)}%`}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              sx={{
                color: theme.palette.grey[900],
                '& .MuiSlider-thumb': {
                  backgroundColor: theme.palette.grey[900],
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0px 0px 0px 8px ${theme.palette.grey[900]}26`,
                  },
                },
                '& .MuiSlider-track': {
                  backgroundColor: theme.palette.grey[900],
                  border: 'none',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: theme.palette.grey[300],
                },
                '& .MuiSlider-mark': {
                  backgroundColor: theme.palette.grey[400],
                },
                '& .MuiSlider-markLabel': {
                  color: theme.palette.grey[600],
                },
                '& .MuiSlider-valueLabel': {
                  backgroundColor: theme.palette.grey[900],
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
}, (prevProps, nextProps) => (
  prevProps.component === nextProps.component &&
  prevProps.config.value === nextProps.config.value &&
  prevProps.config.label === nextProps.config.label &&
  prevProps.config.description === nextProps.config.description &&
  prevProps.normalizedConfig.displayPercentage === nextProps.normalizedConfig.displayPercentage &&
  prevProps.isDragging === nextProps.isDragging
));

ScoringFactorCard.displayName = 'ScoringFactorCard';

export default ScoringFactorCard;
